import React, { useEffect, useState } from "react";
import { Col, Modal, ModalFooter } from "react-bootstrap";
import { Button, DropDown, Input } from "../../components";
import {
  validateCharacterLength,
  validatePositiveValue,
} from "../../utilities/utils";
import { useAppSelector } from "../../redux/Hooks/Hook";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { messages } from "../../utilities/Messages";

const getMessage = messages.posAdmin;

const initSchema = {
  firstName: yup
    .string()
    .required(getMessage.firstName_required)
    .matches(
      /^[a-zA-Z .-]+$/,
      getMessage.firstName_matches
    )
    .min(3, getMessage.firstName_minLength)
    .max(30, getMessage.firstName_maxLength),
  lastName: yup
    .string()
    .required(getMessage.lastName_required)
    .matches(
      /^[a-zA-Z .-]+$/,
      getMessage.lastName_matches
    )
    .min(3, getMessage.lastName_minLength)
    .max(30, getMessage.lastName_maxLength),

  gstNumber: yup
    .string()
    .test('len', getMessage.gstnumber_length, (val) => !val || val.length === 15 || val.length === 0)
    .notRequired(),


  addressLine1: yup
    .string()
    .notRequired()
    .matches(/^[A-Za-z0-9\s,.&-]*$/)
    .max(50, getMessage.addressLine1_maxLength),

  addressLine2: yup
    .string()
    .notRequired()
    .matches(/^[A-Za-z0-9\s,.&-]*$/)
    .max(50, getMessage.addressLine2_maxLength),

  landmark: yup
    .string()
    .notRequired()
    .matches(/^[A-Za-z0-9\s,.&-]*$/)
    .max(50, getMessage.landmark_maxLength),

  companyName: yup
    .string(),

  city: yup
    .string()
    .notRequired()
    .matches(/^[A-Za-z\s]*$/, getMessage.city_match)
    .max(50, getMessage.city_maxLength),

  state: yup
    .string()
    .notRequired()
    .max(50, getMessage.state_maxLength),

  pincode: yup
    .string()
    .notRequired()
    .matches(/^[1-9][0-9]{5}$/, {
      message: getMessage.pincode_maxLength,
      excludeEmptyString: true
    }),


  email: yup.string()
    .test(
      'is-empty-or-valid-email',
      getMessage.email_matches,
      value => !value || yup.string().email().isValidSync(value)
    )
    .notRequired(),
}

type UserDataObjType = {
  firstName: string;
  lastName: string;
  email: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  landmark?: string;
  gstNumber: string;
  companyName: string;
};

const ConfirmBillModal = (props: any) => {
  const {
    show,
    handleClose,
    printing,
    data,
    setData,
    isVerified,
    verifyStatus,
      hanldeCofirmBillPrint
  } = props;
  const { states } = useAppSelector((state: any) => state.states);
  const [phoneError, setPhoneError] = useState("")
  const [schema, setSchema] = useState(initSchema)
  const [state, setState] = useState("")

  const cleanSecondObject = (first: any, second: any) => {
    const updatedSecondObj = { ...second };
    for (const key in first) {
      if (first[key] !== null && first[key] !== undefined && first[key] !== '') {
        updatedSecondObj[key] = yup
          .string()
      }
    }
    return updatedSecondObj;
  };

  useEffect(() => {
    if (verifyStatus) {
      const cleanedObj = cleanSecondObject(isVerified, schema);
      setSchema(cleanedObj);
      reset({
        firstName: isVerified.firstName || "",
        lastName: isVerified.lastName || "",
        email: isVerified.email || "",
        companyName: isVerified.companyName || "",
        gstNumber: isVerified.gstNumber || "",
        addressLine1: isVerified.addressLine1 || "",
        addressLine2: isVerified.addressLine2 || "",
        landmark: isVerified.landmark || "",
        city: isVerified.city || "",
        state: isVerified.state || "",
        pincode: isVerified.pincode || "",
      });
    }
  }, [verifyStatus, isVerified])

  useEffect(() => {
    if (!show) {
      setSchema(initSchema);
      reset({
        firstName: "", lastName: "", email: "", companyName: "",
        gstNumber: "", addressLine1: "", addressLine2: "",
        landmark: "", city: "", state: "", pincode: "",
      });
    }
  }, [show])

  function cleanObject(obj: any) {
    const cleanedObj = { ...obj };
    Object.keys(cleanedObj).forEach(key => {
      if (cleanedObj[key] === '' || cleanedObj[key] == null) {
        delete cleanedObj[key];
      }
    });
    return cleanedObj;
  }


  const UserDataSchema = yup.object().shape(schema);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<UserDataObjType>({
    resolver: yupResolver(UserDataSchema),
  });
  const onSubmit: SubmitHandler<any> = (record) => {
    let updatedData;
    if (verifyStatus) {
      const clenedObj = cleanObject(record);
      updatedData = { ...data, ...isVerified, ...clenedObj };
    } else {
      updatedData = { ...data, ...record };
    }
    setData(updatedData);
    hanldeCofirmBillPrint(updatedData);
  };

  const setValueWithLengthAndPositive = (
    key = "",
    value = "",
    min = 0,
    max = 0
  ) => {
    if (
      validateCharacterLength(value, min, max) &&
      validatePositiveValue(value)
    ) {
      setData({ ...data, [key]: value });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header className="border-0 pb-0 pb-2" closeButton>
          <Modal.Title id="contained-modal-title-vcenter fw-semibold">
            Add Customer Info
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="mobile-number" className="form-label">
                Mobile Number <small className="text-danger">*</small>
              </label>
              <Input
                value={data.phone}
                type="text"
                className="form-control"
                name="phone"
                id="mobile-number"
                onChange={(e: any) => {

                  setValueWithLengthAndPositive("phone", e.target.value, 0, 10)

                  if (data.phone.length < 9 && data.phone.length > 0) {
                    setPhoneError("mobile number must be 10 digits")
                  } else {
                    setPhoneError("")
                  }
                }
                }
                onKeyDown={(e: any) =>
                  (e.key === "e" || e.key === ".") && e.preventDefault()
                }
              />
              <span className="error-msg">{phoneError}</span>
            </div>
            <div className="mb-3 col">
              <label htmlFor="fname" className="form-label">
                First Name <small className="text-danger">*</small>
              </label>
              <Input
                defaultValue={data.firstName}
                type="text"
                className="form-control"
                name="firstName"
                id="fname"
                register={register}
                disabled={isVerified.firstName}
              />
              {errors.firstName && (
                <small className="custom-error-msg">
                  {errors.firstName.message}
                </small>
              )}
            </div>
          </div>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="lname" className="form-label">
                Last Name <small className="text-danger">*</small>
              </label>
              <Input
                defaultValue={data.lastName}
                type="text"
                className="form-control"
                name="lastName"
                id="lname"
                register={register}
                disabled={isVerified.lastName}
              />
              {errors.lastName && (
                <small className="custom-error-msg">
                  {errors.lastName.message}
                </small>
              )}
            </div>
            <div className="mb-3 col">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <Input
                defaultValue={data.email}
                type="email"
                className="form-control"
                name="email"
                id="email"
                register={register}
                disabled={isVerified.email}
              />
              {errors.email && (
                <small className="custom-error-msg">
                  {errors.email.message}
                </small>
              )}
            </div>
          </div>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="companyName" className="form-label">
                Company Name
              </label>
              <Input
                defaultValue={data.companyName}
                type="text"
                className="form-control"
                name="companyName"
                id="companyName"
                register={register}
                disabled={isVerified.companyName}
              />
              {errors.companyName && (
                <small className="custom-error-msg">
                  {errors.companyName.message}
                </small>
              )}
            </div>
            <div className="mb-3 col">
              <label htmlFor="gstNumber" className="form-label">
                GST Number
              </label>
              <Input
                defaultValue={data.gstNumber}
                type="text"
                className="form-control"
                name="gstNumber"
                id="gstNumber"
                register={register}
                disabled={isVerified.gstNumber}
              />
              {errors.gstNumber && (
                <small className="custom-error-msg">
                  {errors.gstNumber.message}
                </small>
              )}
            </div>
          </div>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="addressLine1" className="form-label">
                Address Line 1
                {/* <small className="text-danger">*</small> */}
              </label>
              <Input
                type="text"
                placeholder="Enter Address"
                id="addressLine1"
                name="addressLine1"
                className="form-control"
                defaultValue={data?.addressLine1}
                register={register}
                disabled={isVerified.addressLine1}
              />
              {errors.addressLine1 && (
                <small className="custom-error-msg">
                  {errors.addressLine1.message}
                </small>
              )}
            </div>
            <div className="mb-3 col">
              <label htmlFor="addressLine2" className="form-label">
                Address Line 2
              </label>
              <Input
                type="text"
                placeholder="Enter Address Line 2"
                id="addressLine2"
                name="addressLine2"
                className="form-control"
                defaultValue={data?.addressLine2}
                register={register}
                disabled={isVerified.addressLine2}
              />
              {errors.addressLine2 && (
                <small className="custom-error-msg">
                  {errors.addressLine2.message}
                </small>
              )}
            </div>
          </div>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="landmark" className="form-label">
                Landmark
              </label>
              <Input
                type="text"
                placeholder="Enter Landmark"
                id="landmark"
                name="landmark"
                className="form-control"
                defaultValue={data?.landmark}
                register={register}
                disabled={isVerified.landmark}
              />
              {errors.landmark && (
                <small className="custom-error-msg">
                  {errors.landmark.message}
                </small>
              )}
            </div>
            <div className="mb-3 col">
              <label htmlFor="city" className="form-label">
                City
                {/* <small className="text-danger">*</small> */}
              </label>
              <Input
                type="text"
                placeholder="Enter City"
                id="city"
                name="city"
                className="form-control"
                defaultValue={data?.city}
                register={register}
                disabled={isVerified.city}
              />
              {errors.city && (
                <small className="custom-error-msg">
                  {errors.city.message}
                </small>
              )}
            </div>
          </div>
          <div className="row">
            <div className="mb-3 col">
              <label htmlFor="state" className="form-label">
                State
                {/* <small className="text-danger">*</small> */}
              </label>
              <DropDown
                name="state"
                id="state"
                className="form-control"
                value={data?.state || state}
                onChange={(e: any) => setState(e.target.value)}
                register={register}
                disabled={isVerified.state}
              >
                <option value="">Select State</option>
                {states?.map((item: any) => {
                  return (
                    <option key={item.code} value={item.name}>
                      {item.name}
                    </option>
                  );
                })}
              </DropDown>
              {errors.state && (
                <small className="custom-error-msg">
                  {errors.state.message}
                </small>
              )}
            </div>
            <div className="mb-3 col">
              <label htmlFor="pincode" className="form-label">
                Pincode
                {/* <small className="text-danger">*</small> */}
              </label>
              <Input
                type="text"
                placeholder="Enter pincode"
                id="pincode"
                name="pincode"
                className="form-control"
                defaultValue={data?.pincode}
                register={register}
                onKeyDown={(e: any) =>
                  (e.key === "e" || e.key === ".") && e.preventDefault()
                }
                disabled={isVerified.pincode}
              />
              {errors.pincode && (
                <small className="custom-error-msg">
                  {errors.pincode.message}
                </small>
              )}
            </div>
          </div>
          <ModalFooter>
            <Col lg={12} className="text-center">
              <Button
                type="submit"
                bname={
                  printing
                    ? "Confirmed and Printing Bill"
                    : "Confirm and Print Bill"
                }
                name="print-bill"
                id="print-bill"
                className="btn btn-lg btn-primary rounded-1 mt-1"
                onClick={() => {
                  if (data.phone === "") {
                    setPhoneError("Phone number is required")
                  } else {
                    setPhoneError("")
                  }
                }}
                disabled={printing}
              ></Button>
            </Col>
          </ModalFooter>
        </Modal.Body>
      </form>
    </Modal >
  );
};

export { ConfirmBillModal };
